import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { House, FileText, Calendar, User, SignOut, ChartPie } from "@phosphor-icons/react";

const NAV = [
  { to: "/portal", label: "Dashboard", icon: House, end: true },
  { to: "/portal/portfolio", label: "Portfolio", icon: ChartPie },
  { to: "/portal/documents", label: "Documents", icon: FileText },
  { to: "/portal/appointments", label: "Appointments", icon: Calendar },
  { to: "/portal/profile", label: "Profile", icon: User },
];

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-bone flex">
      <aside className="w-64 bg-midnight text-white p-6 hidden md:flex flex-col">
        <div className="mb-10 bg-white/95 rounded-lg p-2 inline-block">
          <img src="/logo.webp" alt="Manigriv" className="h-10 w-auto" />
        </div>
        <nav className="flex-1 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`portal-nav-${n.label.toLowerCase()}`} className={({isActive}) => `flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${isActive ? "bg-white/10 text-gold" : "text-white/70 hover:bg-white/5"}`}>
              <n.icon size={18} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-6 pt-6 border-t border-white/10">
          <div className="text-sm font-semibold">{user?.name}</div>
          <div className="text-xs text-white/50 mb-3">{user?.email}</div>
          <button data-testid="portal-logout" onClick={async ()=>{await logout(); nav("/");}} className="flex items-center gap-2 text-sm text-white/70 hover:text-gold"><SignOut size={16}/> Logout</button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto">
        <div className="md:hidden bg-midnight text-white p-4 flex items-center justify-between">
          <div className="font-display">Portal</div>
          <button onClick={async ()=>{await logout(); nav("/");}} className="text-sm text-gold">Logout</button>
        </div>
        <Outlet />
      </main>
    </div>
  );
}

export function PortalDashboard() {
  const { user } = useAuth();
  const [appts, setAppts] = useState([]);
  const [docs, setDocs] = useState([]);
  const [pf, setPf] = useState([]);
  useEffect(() => {
    api.get("/appointments").then(r => setAppts(r.data));
    api.get("/documents").then(r => setDocs(r.data));
    api.get("/portfolio").then(r => setPf(r.data));
  }, []);
  const totalInvested = pf.reduce((s,e)=>s+e.invested,0);
  const totalCurrent = pf.reduce((s,e)=>s+e.current_value,0);
  const gain = totalCurrent - totalInvested;
  const gainPct = totalInvested ? (gain/totalInvested)*100 : 0;
  return (
    <div className="p-8 md:p-12">
      <div className="overline text-gold-dark mb-3">Welcome back</div>
      <h1 className="font-display text-4xl text-midnight mb-10">{user?.name?.split(" ")[0]}, here's today.</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <Stat label="Total invested" value={`₹${totalInvested.toLocaleString("en-IN")}`} />
        <Stat label="Current value" value={`₹${totalCurrent.toLocaleString("en-IN")}`} />
        <Stat label="Gain / Loss" value={`${gain>=0?"+":""}₹${Math.abs(gain).toLocaleString("en-IN")} (${gainPct.toFixed(1)}%)`} accent />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-black/5">
          <h3 className="font-display text-xl text-midnight mb-4">Upcoming meetings</h3>
          {appts.slice(0,3).map(a => (
            <div key={a.id} className="py-3 border-b border-black/5 last:border-0">
              <div className="text-sm font-semibold text-midnight">{a.service}</div>
              <div className="text-xs text-midnight/60">{new Date(a.slot).toLocaleString()}</div>
              <div className="mt-1 overline text-[10px] text-gold-dark">{a.status}</div>
            </div>
          ))}
          {appts.length === 0 && <p className="text-sm text-midnight/50">No meetings yet.</p>}
        </div>
        <div className="bg-white rounded-2xl p-6 border border-black/5">
          <h3 className="font-display text-xl text-midnight mb-4">Recent documents</h3>
          {docs.slice(0,4).map(d => (
            <div key={d.id} className="py-3 border-b border-black/5 last:border-0 flex justify-between items-center">
              <div><div className="text-sm font-semibold text-midnight">{d.name}</div><div className="text-xs text-midnight/60">{d.category}</div></div>
              <a href={d.url} target="_blank" rel="noreferrer" className="text-xs text-gold-dark font-semibold">Open</a>
            </div>
          ))}
          {docs.length === 0 && <p className="text-sm text-midnight/50">No documents yet.</p>}
        </div>
      </div>
    </div>
  );
}
function Stat({ label, value, accent }) {
  return <div className={`p-6 rounded-2xl border ${accent ? "bg-midnight text-white border-midnight" : "bg-white border-black/5"}`}>
    <div className={`overline mb-2 ${accent ? "text-gold" : "text-midnight/60"}`}>{label}</div>
    <div className="font-display text-2xl">{value}</div>
  </div>;
}

export function PortalPortfolio() {
  const [pf, setPf] = useState([]);
  useEffect(() => { api.get("/portfolio").then(r=>setPf(r.data)); }, []);
  return (
    <div className="p-8 md:p-12">
      <div className="overline text-gold-dark mb-3">Holdings</div>
      <h1 className="font-display text-4xl text-midnight mb-10">Your portfolio</h1>
      <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-midnight/5 text-left"><tr><th className="p-4">Asset</th><th className="p-4">Type</th><th className="p-4 text-right">Invested</th><th className="p-4 text-right">Current</th><th className="p-4 text-right">Gain</th></tr></thead>
          <tbody>
            {pf.map(e => {
              const g = e.current_value - e.invested;
              return <tr key={e.id} className="border-t border-black/5"><td className="p-4 font-semibold">{e.name}</td><td className="p-4 text-midnight/70">{e.asset_type}</td><td className="p-4 text-right">₹{e.invested.toLocaleString("en-IN")}</td><td className="p-4 text-right">₹{e.current_value.toLocaleString("en-IN")}</td><td className={`p-4 text-right font-semibold ${g>=0?"text-emerald-600":"text-red-600"}`}>{g>=0?"+":""}₹{Math.abs(g).toLocaleString("en-IN")}</td></tr>;
            })}
            {pf.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-midnight/50">No holdings yet. Your advisor will populate this.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function PortalDocuments() {
  const [docs, setDocs] = useState([]);
  useEffect(() => { api.get("/documents").then(r=>setDocs(r.data)); }, []);
  return (
    <div className="p-8 md:p-12">
      <div className="overline text-gold-dark mb-3">Vault</div>
      <h1 className="font-display text-4xl text-midnight mb-10">Documents</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {docs.map(d => (
          <div key={d.id} className="p-6 bg-white rounded-xl border border-black/5 flex items-center justify-between">
            <div><div className="overline text-gold-dark text-[10px] mb-1">{d.category}</div><div className="font-semibold text-midnight">{d.name}</div>{d.notes && <div className="text-xs text-midnight/60 mt-1">{d.notes}</div>}</div>
            <a href={d.url} target="_blank" rel="noreferrer" className="btn-outline-ink text-xs">Open</a>
          </div>
        ))}
        {docs.length === 0 && <p className="text-midnight/50">No documents yet.</p>}
      </div>
    </div>
  );
}

export function PortalAppointments() {
  const [appts, setAppts] = useState([]);
  useEffect(() => { api.get("/appointments").then(r=>setAppts(r.data)); }, []);
  return (
    <div className="p-8 md:p-12">
      <div className="overline text-gold-dark mb-3">Meetings</div>
      <h1 className="font-display text-4xl text-midnight mb-10">Appointments</h1>
      <div className="space-y-3">
        {appts.map(a => (
          <div key={a.id} className="p-6 bg-white rounded-xl border border-black/5 flex flex-wrap gap-4 justify-between items-center">
            <div><div className="font-display text-lg text-midnight">{a.service}</div><div className="text-sm text-midnight/60">{new Date(a.slot).toLocaleString()}</div></div>
            <span className="overline text-[10px] px-3 py-1 rounded-full bg-gold/10 text-gold-dark">{a.status}</span>
          </div>
        ))}
        {appts.length === 0 && <p className="text-midnight/50">No appointments yet.</p>}
      </div>
    </div>
  );
}

export function PortalProfile() {
  const { user } = useAuth();
  return (
    <div className="p-8 md:p-12 max-w-2xl">
      <div className="overline text-gold-dark mb-3">Account</div>
      <h1 className="font-display text-4xl text-midnight mb-10">Profile</h1>
      <div className="bg-white rounded-2xl border border-black/5 p-8 space-y-4">
        <Row label="Name" value={user?.name} />
        <Row label="Email" value={user?.email} />
        <Row label="Phone" value={user?.phone || "—"} />
        <Row label="Role" value={user?.role} />
      </div>
    </div>
  );
}
function Row({ label, value }) {
  return <div className="flex justify-between border-b border-black/5 pb-3 last:border-0"><span className="overline text-midnight/60">{label}</span><span className="text-sm text-midnight">{value}</span></div>;
}
